import { Reducer } from 'react';
import {
  CampaignEditorAction,
  CampaignEditorState,
  PurchaseCheckUnit,
  TargetAudienceType,
  TargetLookbackPeriod,
} from './CampaignEditorState';
import { OffsiteCampaignStatus } from 'models/offsite/Campaign/LifeCycle';
import { Moment } from 'moment';
import moment from 'moment';

export const campaignEditorDefaultState: CampaignEditorState = Object.freeze({
  status: OffsiteCampaignStatus.Draft,
  hasUnsavedChanges: false,
  drawerOpen: false,
  campaign: Object.freeze({
    name: '',
    targetAudience: [],
    purchaseCheckDays: {},
    enablePurchaseCheck: {},
    purchaseCheck: {},
    additionalSegmentation: {
      [TargetAudienceType.BasketAbandonment]: [[]],
      [TargetAudienceType.BrowseAbandonment]: [[]],
      [TargetAudienceType.Purchase]: [[]],
    },
    ids: {
      targetAudience: {},
      segmentId: {},
      percentageSplitTestId: {},
      cycleId: {},
      purchaseCheckId: {},
    },
    template: {},
    subjectLine: {},
    bulkSortByFeature: false,
  }),
});

const editCampaign = (
  state: CampaignEditorState,
  newState: Partial<CampaignEditorState['campaign']>,
): CampaignEditorState => {
  return {
    ...state,
    campaign: {
      ...state.campaign,
      ...newState,
    },
    hasUnsavedChanges: true,
  };
};

/**
 * Calculate minSendDate and maxSendDate based on targetAudiences
 * - minSendDate: latest (maximum) endDate among all targetAudiences + 1 day
 * - maxSendDate: earliest (minimum) startDate among all targetAudiences + 180 days
 */
export const calculateSendDateBounds = (
  targetAudiences: CampaignEditorState['campaign']['targetAudience'],
): { minSendDate?: Moment; maxSendDate?: Moment } => {
  const endDates = targetAudiences
    .map(audience => audience.endDate)
    .filter((date): date is Moment => date !== undefined && date !== null);

  const startDates = targetAudiences
    .map(audience => audience.startDate)
    .filter((date): date is Moment => date !== undefined && date !== null);

  const minSendDate =
    endDates.length > 0
      ? moment
          .utc(
            moment
              .max(...endDates)
              .clone()
              .add(1, 'days')
              .format('YYYY-MM-DD'),
          )
          .startOf('day')
          .local()
      : undefined;
  const earliestStartDate = startDates.length > 0 ? moment.min(...startDates) : undefined;
  const maxSendDate = earliestStartDate ? earliestStartDate.clone().add(181, 'days') : undefined;

  return { minSendDate, maxSendDate };
};

export const campaignEditorReducer: Reducer<CampaignEditorState, CampaignEditorAction> = (state, action) => {
  switch (action.type) {
    case 'SET_CAMPAIGN_NAME': {
      return editCampaign(state, { name: action.payload.name });
    }
    case 'SET_TARGET_AUDIENCE': {
      const updateTargetAudience = (a: typeof action, s: typeof state) => {
        const existingTargetAudience = s.campaign.targetAudience;
        const targetAudience = {
          audience: a.payload.audience,
          days: a.payload.days,
          dateType: a.payload.dateType,
          startDate: a.payload.startDate,
          endDate: a.payload.endDate,
          isUsingDateRange: a.payload.isUsingDateRange,
        };
        if (!existingTargetAudience[a.payload.index]) {
          return existingTargetAudience;
        }
        const duplicateItem = existingTargetAudience.find(ad => ad.audience === a.payload.audience);
        if (duplicateItem) {
          const duplicateItemIndex = existingTargetAudience.indexOf(duplicateItem);
          existingTargetAudience[duplicateItemIndex].audience = existingTargetAudience[a.payload.index].audience;
        }

        existingTargetAudience[a.payload.index] = targetAudience;
        return existingTargetAudience;
      };

      const updatedTargetAudience = updateTargetAudience(action, state);
      const { minSendDate, maxSendDate } = calculateSendDateBounds(updatedTargetAudience);

      let newSendDate = state.campaign.sendDate;

      if (minSendDate !== undefined && (!newSendDate || newSendDate.getTime() === 0)) {
        // If the campaign hasn't set a schedule date yet, AND we have a max send date present from a date range
        // Set the scheduled date to the earliest possible date we can choose from
        newSendDate = minSendDate.toDate();

        // Basically, this logic won't trigger if we are using the day/month sliders
        // Also won't trigger is a scheduled date is set already - don't want to override it for no reason!
      }

      const audience = action.payload.audience;
      return editCampaign(state, {
        targetAudience: updatedTargetAudience,
        sendDate: newSendDate,
        minSendDate: minSendDate,
        maxSendDate: maxSendDate,
        enablePurchaseCheck: {
          ...state.campaign.enablePurchaseCheck,
          [audience]:
            audience === TargetAudienceType.Purchase ? false : state.campaign.enablePurchaseCheck[audience] ?? false,
        },
      });
    }
    case 'ADD_TARGET_AUDIENCE': {
      if (state.campaign.targetAudience.length >= 3) {
        return state;
      }

      const defaultTargetAudience = Object.values(TargetAudienceType).filter(
        audienceType => !state.campaign.targetAudience.some(x => x.audience === audienceType),
      );

      const targetAudience = {
        audience: defaultTargetAudience[0],
        days: 30,
        dateType: TargetLookbackPeriod.Days,
      };
      const audience = targetAudience.audience;
      const enablePurchaseCheckValue = audience !== TargetAudienceType.Purchase;

      const updatedTargetAudience = [...state.campaign.targetAudience, targetAudience];
      const { minSendDate, maxSendDate } = calculateSendDateBounds(updatedTargetAudience);

      return editCampaign(state, {
        targetAudience: updatedTargetAudience,
        minSendDate: minSendDate,
        maxSendDate: maxSendDate,
        purchaseCheckDays: { ...state.campaign.purchaseCheckDays, [audience]: 1 },
        enablePurchaseCheck: {
          ...state.campaign.enablePurchaseCheck,
          [audience]: enablePurchaseCheckValue,
        },
        purchaseCheck: {
          ...(state.campaign.purchaseCheck || {}),
          [audience]: {
            enablePurchaseCheck: enablePurchaseCheckValue,
            purchaseCheckDays: 1, // Default value, same pattern as target audience days: 30
            purchaseCheckMonths: 1, // Default value
            purchaseCheckUnit: PurchaseCheckUnit.Days,
            purchaseCheckStartDate: null,
            purchaseCheckEndDate: null,
            isPurchaseCheckUsingDateRange: false,
          },
        },
      });
    }
    case 'DELETE_TARGET_AUDIENCE': {
      const deleteTargetAudience = (a: typeof action, s: typeof state) => {
        if (s.campaign.targetAudience.length < 1) {
          return s.campaign.targetAudience;
        }

        // Create a new array, omitting the element at the specified index
        return [
          ...s.campaign.targetAudience.slice(0, a.payload.index),
          ...s.campaign.targetAudience.slice(a.payload.index + 1),
        ];
      };
      const updatedTargetAudience = deleteTargetAudience(action, state);
      const { minSendDate, maxSendDate } = calculateSendDateBounds(updatedTargetAudience);
      return editCampaign(state, {
        targetAudience: updatedTargetAudience,
        maxSendDate: maxSendDate,
        minSendDate: minSendDate,
      });
    }
    case 'SET_ADDITIONAL_SEGMENTATION': {
      return editCampaign(state, {
        additionalSegmentation: { ...state.campaign.additionalSegmentation, ...action.payload },
      });
    }
    case 'SET_PURCHASE_CHECK': {
      const updatedPurchaseCheck = { ...(state.campaign.purchaseCheck || {}) };
      const updatedDays = mapPurchaseCheckToDays(state.campaign.purchaseCheckDays, action.payload);

      // Update new structure if it exists, or create it if it doesn't
      for (const audience in action.payload) {
        updatedPurchaseCheck[audience] = {
          ...(updatedPurchaseCheck[audience] || {
            purchaseCheckDays: state.campaign.purchaseCheckDays[audience] ?? 1,
            purchaseCheckMonths: 1,
            purchaseCheckUnit: PurchaseCheckUnit.Days,
            purchaseCheckStartDate: null,
            purchaseCheckEndDate: null,
            isPurchaseCheckUsingDateRange: false,
          }),
          enablePurchaseCheck: action.payload[audience] ?? false,
          purchaseCheckDays: updatedDays[audience] ?? updatedPurchaseCheck[audience]?.purchaseCheckDays ?? 1,
        };
      }

      return editCampaign(state, {
        enablePurchaseCheck: { ...state.campaign.enablePurchaseCheck, ...action.payload },
        purchaseCheckDays: {
          ...state.campaign.purchaseCheckDays,
          ...updatedDays,
        },
        purchaseCheck: updatedPurchaseCheck,
      });
    }
    case 'SET_PURCHASE_CHECK_DAYS': {
      const updatedPurchaseCheck = { ...(state.campaign.purchaseCheck || {}) };

      // Update new structure if it exists, or create it if it doesn't
      for (const audience in action.payload) {
        updatedPurchaseCheck[audience] = {
          ...(updatedPurchaseCheck[audience] || {
            enablePurchaseCheck: state.campaign.enablePurchaseCheck[audience] ?? false,
            purchaseCheckMonths: 1,
            purchaseCheckUnit: PurchaseCheckUnit.Days,
            purchaseCheckStartDate: null,
            purchaseCheckEndDate: null,
            isPurchaseCheckUsingDateRange: false,
          }),
          purchaseCheckDays: action.payload[audience] ?? updatedPurchaseCheck[audience]?.purchaseCheckDays ?? 1,
        };
      }

      return editCampaign(state, {
        purchaseCheckDays: { ...state.campaign.purchaseCheckDays, ...action.payload },
        purchaseCheck: updatedPurchaseCheck,
      });
    }
    case 'SET_PURCHASE_CHECK_ALL': {
      const { targetAudience, ...purchaseCheckData } = action.payload;
      const audience = targetAudience.audience;
      return editCampaign(state, {
        enablePurchaseCheck: {
          ...state.campaign.enablePurchaseCheck,
          [audience]: purchaseCheckData.enablePurchaseCheck,
        },
        purchaseCheckDays: {
          ...state.campaign.purchaseCheckDays,
          [audience]: purchaseCheckData.purchaseCheckDays,
        },
        purchaseCheck: {
          ...(state.campaign.purchaseCheck || {}),
          [audience]: {
            enablePurchaseCheck: purchaseCheckData.enablePurchaseCheck,
            purchaseCheckDays: purchaseCheckData.purchaseCheckDays,
            purchaseCheckMonths: purchaseCheckData.purchaseCheckMonths,
            purchaseCheckUnit: purchaseCheckData.purchaseCheckUnit,
            purchaseCheckStartDate: purchaseCheckData.purchaseCheckStartDate,
            purchaseCheckEndDate: purchaseCheckData.purchaseCheckEndDate,
            isPurchaseCheckUsingDateRange: purchaseCheckData.isPurchaseCheckUsingDateRange,
          },
        },
      });
    }
    case 'SET_SEND_DATE': {
      return editCampaign(state, { sendDate: action.payload });
    }
    case 'SET_TEMPLATE': {
      return editCampaign(state, { template: { ...state.campaign.template, ...action.payload } });
    }
    case 'SET_SUBJECT_LINE': {
      return editCampaign(state, { subjectLine: { ...state.campaign.subjectLine, ...action.payload } });
    }
    case 'SET_SORT_OPTION': {
      return editCampaign(state, { bulkSortByFeature: action.payload });
    }
    case 'SET_STATUS': {
      let status = action.payload;
      const isImmediate = !state?.campaign.sendDate && action.payload === OffsiteCampaignStatus.Scheduled;

      if (isImmediate) {
        status = OffsiteCampaignStatus.Triggered;
      }

      return {
        ...state,
        status,
        readOnly: status !== OffsiteCampaignStatus.Draft,
      };
    }
    case 'SET_DRAWER_OPEN': {
      return {
        ...state,
        drawerOpen: action.payload,
      };
    }
    case 'SET_UNSAVED_CHANGES': {
      return {
        ...state,
        hasUnsavedChanges: action.payload,
      };
    }
    default:
      return state;
  }
};

function mapPurchaseCheckToDays(
  days: { [key in TargetAudienceType]?: number },
  check: { [key in TargetAudienceType]?: boolean },
): { [key in TargetAudienceType]?: number } {
  const newDays: { [key in TargetAudienceType]?: number } = {};
  for (const key in check) {
    // Default to 1 instead of 30 to match getDefaultPurchaseCheckState()
    check[key] && (days[key] ? (newDays[key] = days[key]) : (newDays[key] = 1));
  }
  return newDays;
}
